import { marketGuidesEnabled } from 'src/app/site-config';
import { AfterViewInit, Component, ElementRef, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { seriesMeta, seriesPeriodLabel } from 'src/app/utils/series-url';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FixturePhaseResult,
  NewReleasePageService,
} from './services/new-release-page.service';
import {
  NewReleasePageResponse,
  PerfectPlatesSection,
  PlateCard,
} from './models/new-release-page.types';
import { SectionPill } from './components/new-release-section-nav/new-release-section-nav.component';
import { ByoSubmit } from './components/new-release-byo/new-release-byo.component';
import { SessionService } from 'src/app/services/session-service';
import { QuickSignupService } from 'src/app/services/quick-signup-service';
import { SavedPlatesService } from 'src/app/services/saved-plates.service';
import { TrackPlateService } from 'src/app/services/track-plate.service';
import { Registration } from 'src/app/models/registration';
import { SearchService, SearchReqResponse } from 'src/app/services/search-service';
import { RegistrationService } from 'src/app/services/registration-service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';

type PageState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: NewReleasePageResponse }
  | { kind: 'unavailable'; year: string };

export type SortKey =
  | 'quality_desc'
  | 'quality_asc'
  | 'price_asc'
  | 'price_desc'
  | 'a_z'
  | 'z_a';

export const SORT_OPTIONS: Array<{ value: SortKey; label: string }> = [
  { value: 'quality_desc', label: 'Quality \u2193' },
  { value: 'quality_asc',  label: 'Quality \u2191' },
  { value: 'price_asc',    label: 'Price \u2191' },
  { value: 'price_desc',   label: 'Price \u2193' },
  { value: 'a_z',          label: 'A to Z' },
  { value: 'z_a',          label: 'Z to A' },
];

/** Sticky chapter-nav entry (dev2 Register pattern). `ix` is the roman
 *  numeral shown in the section eyebrow, assigned in page order. */
export interface NewReleaseChapter {
  id: string;
  label: string;
  ix: string;
}

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

@Component({
  selector: 'pla-new-release-page-v2',
  templateUrl: './new-release-page-v2.component.html',
  styleUrls: ['./new-release-page-v2.component.scss'],
})
export class NewReleasePageV2Component implements OnInit, AfterViewInit, OnDestroy {
  /** Market Guides is on for this host: the sign-up panel mentions the free guide. */
  public readonly guidesOn = marketGuidesEnabled;
  releaseYear: string = '76';
  state: PageState = { kind: 'loading' };
  sectionPills: SectionPill[] = [];
  chapters: NewReleaseChapter[] = [];

  /** Active A-Z filter for the names grid. Null = show all. */
  selectedLetter: string | null = null;

  /** Per-section visible card count for client-side Load-more. Keyed by
   *  Perfect-Plates section id, plus 'names' for the names grid. Capped at
   *  the size of the underlying cards array (the paginated endpoint will
   *  replace this with real fetch-more once it lands). */
  private readonly PAGE_SIZE = 12;
  visibleCounts: Record<string, number> = {};

  /** Per-section sort key. Default 'quality_desc' across all sections, keyed
   *  by section id (plus 'names' for the names grid). Session-scoped — no
   *  persistence across page loads, matching Hugh's spec. */
  sortKeys: Record<string, SortKey> = {};
  readonly sortOptions = SORT_OPTIONS;


  /** Inline BYO results — same `SearchReqResponse` shape the legacy
   *  /build-your-own page produces, so we can pass it straight into the
   *  canonical `<pla-search-result-table>` for byte-for-byte identical
   *  results rendering (plate / favourite / plate-viewer / price / source /
   *  View button row layout, pagination, sort). */
  byoResults: SearchReqResponse | null = null;
  byoResultChange: EventEmitter<SearchReqResponse> = new EventEmitter<SearchReqResponse>();
  byoLoading: boolean = false;
  byoSearchTerm: string = '';

  /** Banned section — collapsed by default. Each category group shows 9
   *  sample plates + a "+N more" counter; clicking "Show all N banned plates"
   *  expands a flat list of the full FOI feed (banned-76.ts) below. */
  bannedExpanded: boolean = false;
  /** The are-you-sure popup in front of the banned lists. */
  bannedWarning: boolean = false;

  /** Readings for the launch replay's captions (plate without spaces -> word), from the
   *  perfect and names lists. Rebuilt only when the page data changes. */
  get plateTerms(): Record<string, string> {
    if (this.termsFor !== this.data) {
      this.termsFor = this.data;
      const out: Record<string, string> = {};
      const put = (c: { plate: string; word: string }) => { if (c?.word) out[c.plate.replace(/\s+/g, '')] = c.word; };
      this.data?.perfectPlates?.sections?.forEach((sec) => sec.cards.forEach(put));
      this.data?.names?.cards?.forEach(put);
      this.terms = out;
    }
    return this.terms;
  }
  private termsFor: unknown = null;
  private terms: Record<string, string> = {};

  /** Series switcher (plate rail). Draft until signed off: shows on dev2
   *  hosts and on /new-release-draft only, never on the prod canonical URL. */
  showYearRail = false;
  /** Route prefix the rail's links keep the reader on. */
  yearRailBase = 'series';
  /** Canonical /plates/{NN}-reg route: H1, breadcrumbs and the series explainer. */
  isSeriesRoute = false;
  seriesHeading: string | null = null;
  seriesPeriod = '';
  private paramSub?: Subscription;
  private pageSub?: Subscription;
  private sessionSub?: Subscription;

  /** Sign-in gate for a pre-release series (27). Draft until signed off,
   *  same flag as the year rail: dev2 hosts and /new-release-draft only.
   *  Signed out, the page leads with the open half (countdown, auction,
   *  banned) and the fixed-price lists are replaced by a locked teaser.
   *  NOT a security boundary: the lists still ship in the public asset
   *  assets/data/new-release/{year}.json until an authenticated endpoint
   *  serves them (see the PR). */
  private gateFlag = false;
  signedIn = false;
  /** Placeholder plates behind the blur. Never real list entries, so the
   *  locked teaser leaks nothing from the DOM. */
  readonly teaserPlates = ['AB27 CDE', 'FG27 HIJ', 'KL27 MNO', 'PR27 STU', 'VW27 XYZ', 'BC27 DEF', 'GH27 JKL', 'MN27 OPR',
    'CD27 EFG', 'HJ27 KLM', 'NO27 PRS', 'TU27 VWX', 'EF27 GHJ', 'LM27 NOP', 'ST27 UVW', 'XY27 ZAB'];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private pageService: NewReleasePageService,
    private searchService: SearchService,
    private registrationService: RegistrationService,
    private snackBar: MatSnackBar,
    private el: ElementRef<HTMLElement>,
    private sessionService: SessionService,
    private quickSignup: QuickSignupService,
    private trackPlate: TrackPlateService,
    private savedPlates: SavedPlatesService,
  ) {}

  /**
   * Site-canonical plate formatter for any registration coming from an API
   * boundary. Always re-formats from the unspaced raw — never trusts a
   * pre-formatted `formattedRegistration` field, since stale rows can ship
   * with empty/wrong spacing. Mirrors how the legacy plate-detail page
   * derives its display value.
   */
  formatPlate(reg: { registration?: string; formattedRegistration?: string } | null | undefined): string {
    if (!reg) return '';
    const raw = (reg.registration || reg.formattedRegistration || '').replace(/\s+/g, '');
    return raw ? this.registrationService.LocalFormatRegistration(raw) : '';
  }

  /** "1 March 2026 to 31 August 2026" / "1 September 2026 to 28 February 2027". */
  get seriesDates(): string {
    const n = parseInt(this.releaseYear, 10);
    if (n >= 51) {
      const y = 2000 + n - 50;
      const leap = (y + 1) % 4 === 0 && ((y + 1) % 100 !== 0 || (y + 1) % 400 === 0);
      return `1 September ${y} to ${leap ? 29 : 28} February ${y + 1}`;
    }
    return `1 March ${2000 + n} to 31 August ${2000 + n}`;
  }

  /** The other identifier issued in the same year (26 <-> 76). */
  get sisterSeries(): string {
    const n = parseInt(this.releaseYear, 10);
    return String(n >= 51 ? n - 50 : n + 50).padStart(2, '0');
  }

  ngOnInit(): void {
    const isDraftRoute = this.route.snapshot.routeConfig?.path?.startsWith('new-release-draft') || false;
    this.yearRailBase = isDraftRoute ? '/new-release-draft' : 'series';
    this.isSeriesRoute = !!this.route.snapshot.data?.series;
    this.showYearRail = true;
    this.gateFlag = this.showYearRail;
    this.signedIn = this.sessionService.isLoggedIn();
    // Sign in / out anywhere (gate popup, nav) re-arranges the page in place.
    this.sessionSub = this.sessionService.sessionStateChange.subscribe(() => this.refreshSignedIn());

    // The year rail navigates between series on the same route, which
    // reuses this component, so reload on every param change rather than
    // reading the snapshot once.
    this.paramSub = this.route.paramMap.subscribe((params) => {
      this.loadYear(params.get('releaseYear') || '76');
    });

    // Track state comes from the shared tracking system (TrackPlateService);
    // saved state from the shared shortlist (SavedPlatesService).
    this.trackPlate.refresh(false);
  }

  private loadYear(year: string): void {
    this.releaseYear = year;
    this.seriesHeading = this.isSeriesRoute ? seriesMeta(year).heading : null;
    this.seriesPeriod = seriesPeriodLabel(year);
    this.state = { kind: 'loading' };
    this.sectionPills = [];
    this.chapters = [];
    this.selectedLetter = null;
    this.sortKeys = {};
    this.bannedExpanded = false;
    this.bannedWarning = false;
    this.clearByoSearch();
    this.pageSub?.unsubscribe();
    this.pageSub = this.pageService.getPage(year).subscribe({
      next: (response: FixturePhaseResult) => {
        if (response.status === 'ok') {
          this.state = { kind: 'ready', data: response.data };
          this.sectionPills = this.buildPills(response.data);
          this.chapters = this.buildChapters(response.data);
          this.initVisibleCounts(response.data);
          this.scheduleUnlock(response.data);
        } else {
          this.state = { kind: 'unavailable', year: response.year };
          this.sectionPills = [];
        }
      },
      // The service already catchErrors at the pipeline level, but adding an
      // error handler here means any RxJS-level surprise that escapes upstream
      // gets logged rather than silently leaving the page stuck on 'loading'.
      error: (err) => {
        console.error('[new-release] subscribe error', err);
        this.state = { kind: 'unavailable', year: this.releaseYear };
        this.sectionPills = [];
      },
    });
  }

  isFavourited(card: PlateCard | null | undefined): boolean {
    if (!card?.plate) return false;
    return this.savedPlates.isSaved(card.plate);
  }

  /** Bound versions for child components (Likely held back cards). */
  readonly isTrackedFn = (c: PlateCard) => this.isTracked(c);
  readonly isFavouritedFn = (c: PlateCard) => this.isFavourited(c);

  isTracked(card: PlateCard | null | undefined): boolean {
    if (!card?.plate) return false;
    return this.trackPlate.isTracked(card.plate);
  }

  get data(): NewReleasePageResponse | null {
    return this.state.kind === 'ready' ? this.state.data : null;
  }
  /** Signed in but before the members' unlock (27: 18:00 Tue 3 Nov): the
   *  plate sections stay obscured with a "You're in" card. Admins always see
   *  the lists. The API enforces the time; the clock check here covers an
   *  asset that still ships the lists (dev2 until the endpoint is live). */
  get locked(): boolean {
    const s = this.data?.series;
    if (!this.signedIn || !s?.signInGate || !(this.gateFlag || s.listsServerGated)) return false;
    if (this.sessionService.isAdmin()) return false;
    if (s.listsLocked) return true;
    const at = s.listsUnlockAt ? new Date(s.listsUnlockAt).getTime() : NaN;
    return !isNaN(at) && Date.now() < at;
  }

  private unlockTimer?: ReturnType<typeof setTimeout>;

  /** Reload once the members' unlock passes (only when it is under a day away). */
  private scheduleUnlock(data: NewReleasePageResponse): void {
    if (this.unlockTimer) clearTimeout(this.unlockTimer);
    const at = data.series.listsUnlockAt ? new Date(data.series.listsUnlockAt).getTime() : NaN;
    const wait = at - Date.now();
    if (!isNaN(at) && wait > 0 && wait < 86_400_000) {
      this.unlockTimer = setTimeout(() => this.loadYear(this.releaseYear), wait + 2000);
    }
  }

  /** Plate sections obscured: signed out, or signed in before the unlock. */
  get obscured(): boolean { return this.gated || this.locked; }

  /** "18:00 on Tuesday 3 November" in UK time. */
  get unlockLabel(): string {
    const iso = this.data?.series.listsUnlockAt;
    const at = iso ? new Date(iso) : null;
    if (!at || isNaN(at.getTime())) return '';
    const parts: Record<string, string> = {};
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London', weekday: 'long', day: 'numeric', month: 'long',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(at).forEach((p) => { parts[p.type] = p.value; });
    return `${parts['hour']}:${parts['minute']} on ${parts['weekday']} ${parts['day']} ${parts['month']}`;
  }

  /** True when this reader gets the signed-out arrangement. */
  get gated(): boolean {
    const s = this.data?.series;
    // Once the lists are served by the API a signed-out reader has nothing
    // to show below, so the gate applies wherever that asset ships.
    return !this.signedIn && !!s?.signInGate && (this.gateFlag || !!s.listsServerGated);
  }

  /** Which obscured plate sections to show signed out: from the loaded
   *  lists, or the public asset's counts when the API serves them. */
  get gatedLists(): { perfect: boolean; names: boolean } {
    const d = this.data;
    if (!d) return { perfect: false, names: false };
    return {
      perfect: d.perfectPlates.totalCount > 0 || !!d.series.listsPerfect,
      names: d.names.totalCount > 0 || !!d.series.listsNames,
    };
  }

  private refreshSignedIn(): void {
    const now = this.sessionService.isLoggedIn();
    if (now === this.signedIn) return;
    this.signedIn = now;
    // Gated lists come from the API with the reader's token: refetch on sign
    // in (and drop them on sign out).
    if (this.data?.series.listsServerGated) { this.loadYear(this.releaseYear); return; }
    if (this.data) this.chapters = this.buildChapters(this.data);
  }

  /** Gate popup buttons: the site's sign-in / register modal, so the reader
   *  stays on this page. The success route covers the Apple sign-in path,
   *  which leaves the site and lands back on /login. */
  openGateSignIn(startOn: 'login' | 'register'): void {
    this.sessionService.setSuccessRoute(this.router.url);
    this.quickSignup.quickSignup(
      `new-release-${this.releaseYear}`,
      undefined,
      () => this.refreshSignedIn(),
      () => this.refreshSignedIn(),
      undefined,
      startOn,
    );
  }

  get isUnavailable(): boolean { return this.state.kind === 'unavailable'; }
  get isLoading(): boolean { return this.state.kind === 'loading'; }

  private buildPills(data: NewReleasePageResponse): SectionPill[] {
    const pills: SectionPill[] = [];
    if (data.perfectPlates?.totalCount > 0) {
      pills.push({ id: 'perfect', label: 'Perfect plates', count: data.perfectPlates.totalCount });
    }
    if (data.names?.totalCount > 0) {
      pills.push({ id: 'names', label: `${data.series.yearDisplay} in names`, count: data.names.totalCount });
    }
    if (data.banned?.totalCount > 0) {
      pills.push({ id: 'banned', label: 'Banned', count: data.banned.totalCount });
    }
    if (data.faqs?.length > 0) {
      pills.push({ id: 'qa', label: 'Q&As' });
    }
    return pills;
  }

  /** Chapter nav + section numerals, in page order. Mirrors each section's
   *  own render condition so the nav never links to a hidden section. */
  private buildChapters(data: NewReleasePageResponse): NewReleaseChapter[] {
    const out: Array<{ id: string; label: string }> = [];
    // Same tabs signed in or out: signed out, the plate sections are obscured
    // in place, and may be counted from the public asset alone.
    if (data.perfectPlates?.totalCount > 0 || data.series.listsPerfect) out.push({ id: 'perfect', label: 'Perfect plates' });
    if (data.names?.totalCount > 0 || data.series.listsNames) out.push({ id: 'names', label: `${data.series.yearDisplay} in names` });
    const hta = data.headingToAuction;
    if ((hta?.marqueePicks?.length || 0) + (hta?.namesAuction?.length || 0) > 0) {
      out.push({ id: 'heading-to-auction', label: data.headingToAuction?.predicted ? 'Likely held back' : data.headingToAuction?.results ? (data.headingToAuction.heldCount ? 'Auction plates' : 'Sold at auction') : 'Heading to auction' });
    }
    if (data.firstSold?.entries?.length) out.push({ id: 'first-sold', label: 'First sold' });
    out.push({ id: 'banned', label: 'Banned' });
    if (data.faqs?.length > 0) out.push({ id: 'qa', label: 'Q&As' });
    return out.map((c, i) => ({ ...c, ix: ROMAN[i] || String(i + 1) }));
  }

  /** Roman numeral for a section id ('' when the section isn't rendered). */
  ix(id: string): string {
    return this.chapters.find((c) => c.id === id)?.ix || '';
  }

  /** Alternate paper / tint bands down the page, by chapter order, so the
   *  rhythm holds whichever optional sections a series has. */
  tint(id: string): boolean {
    return this.chapters.findIndex((c) => c.id === id) % 2 === 1;
  }

  trackByChapter(_: number, c: NewReleaseChapter): string { return c.id; }

  jumpTo(id: string, ev?: Event): void {
    if (ev) ev.preventDefault();
    const target = this.el.nativeElement.querySelector('#' + id) as HTMLElement | null;
    if (!target) return;
    const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }

  /* Shell measurement — the Register-page pattern (initials/names/number-ones):
     the sticky chapter nav sits under the fixed nav shell, whose height is
     only known at runtime. */
  ngAfterViewInit(): void {
    [0, 150, 500, 1200].forEach((t) => setTimeout(() => this.measureShell(), t));
    window.addEventListener('resize', this.measureShellBound, { passive: true });
  }

  ngOnDestroy(): void {
    this.paramSub?.unsubscribe();
    this.pageSub?.unsubscribe();
    this.sessionSub?.unsubscribe();
    if (this.unlockTimer) clearTimeout(this.unlockTimer);
    window.removeEventListener('resize', this.measureShellBound);
  }

  private measureShellBound = (): void => {
    requestAnimationFrame(() => this.measureShell());
    setTimeout(() => this.measureShell(), 250);
  };

  private measureShell(): void {
    const shell = (document.querySelector('pla-nav-shell') ||
      document.querySelector('.site-header-container .site-header')) as HTMLElement | null;
    let h = 0;
    if (shell) {
      const pos = getComputedStyle(shell).position;
      h = pos === 'fixed' || pos === 'sticky' ? Math.round(shell.getBoundingClientRect().height) : 0;
    }
    this.el.nativeElement.style.setProperty('--shellH', `${h}px`);
  }

  observeSelectors(): string[] {
    return this.sectionPills.map((p) => `#${p.id}`);
  }

  private initVisibleCounts(data: NewReleasePageResponse): void {
    const counts: Record<string, number> = {};
    data.perfectPlates.sections.forEach((s) => {
      // Repeating letters opens on one full row (OO27 OOO, XX27 XXX + two); Load more for the rest.
      counts[s.id] = Math.min(s.id === 'repeating' ? 4 : this.PAGE_SIZE, s.cards.length);
    });
    counts['names'] = Math.min(this.PAGE_SIZE, data.names.cards.length);
    this.visibleCounts = counts;
  }

  visibleSectionCards(section: PerfectPlatesSection): PlateCard[] {
    const sorted = this.sortCards(section.cards, this.sortKeys[section.id] || 'quality_desc');
    return sorted.slice(0, this.visibleCounts[section.id] || this.PAGE_SIZE);
  }

  visibleNameCards(): PlateCard[] {
    if (!this.data) return [];
    const sorted = this.sortCards(this.filteredNames(), this.sortKeys['names'] || 'quality_desc');
    return sorted.slice(0, this.visibleCounts['names'] || this.PAGE_SIZE);
  }

  /** Returns a NEW sorted copy of the cards array — never mutates the input. */
  private sortCards(cards: PlateCard[], key: SortKey): PlateCard[] {
    const out = [...cards];
    const priceVal = (p: number | null) => (p == null ? Number.POSITIVE_INFINITY : p);
    switch (key) {
      case 'quality_asc':  return out.sort((a, b) => a.qualityScore - b.qualityScore);
      case 'price_asc':    return out.sort((a, b) => priceVal(a.price) - priceVal(b.price));
      case 'price_desc':   return out.sort((a, b) => priceVal(b.price) - priceVal(a.price));
      case 'a_z':          return out.sort((a, b) => (a.word || '').localeCompare(b.word || ''));
      case 'z_a':          return out.sort((a, b) => (b.word || '').localeCompare(a.word || ''));
      case 'quality_desc':
      default:             return out.sort((a, b) => b.qualityScore - a.qualityScore);
    }
  }

  setSort(sectionId: string, key: SortKey): void {
    this.sortKeys[sectionId] = key;
    // Re-sorting can't make MORE cards available, but if the user changes sort
    // while only a few cards are visible, reset visible count to the page size
    // so they see the new "top" of the sorted list.
    this.visibleCounts[sectionId] = this.PAGE_SIZE;
  }

  filteredNames(): PlateCard[] {
    if (!this.data) return [];
    if (!this.selectedLetter) return this.data.names.cards;
    const letter = this.selectedLetter.toUpperCase();
    return this.data.names.cards.filter((c) => (c.word || '').toUpperCase().startsWith(letter));
  }

  hasMore(sectionId: string, totalAvailable: number): boolean {
    const visible = this.visibleCounts[sectionId] || 0;
    return visible < totalAvailable;
  }

  loadMoreSection(section: PerfectPlatesSection): void {
    // Preserve scroll position — the button is below the grid; revealing more
    // cards above shouldn't push the viewport. Browsers handle this naturally
    // since the page's flow grows downward; no extra logic needed.
    const current = this.visibleCounts[section.id] || 0;
    this.visibleCounts[section.id] = Math.min(current + this.PAGE_SIZE, section.cards.length);
  }

  loadMoreNames(): void {
    const current = this.visibleCounts['names'] || 0;
    const total = this.filteredNames().length;
    this.visibleCounts['names'] = Math.min(current + this.PAGE_SIZE, total);
  }

  /** Counter copy for Load-more button — "Showing 12 of 96" style. */
  sectionShowingCount(sectionId: string): number {
    return this.visibleCounts[sectionId] || 0;
  }

  /** "Load N more" text — N is whichever is smaller: the remaining fixture cards
   *  or the gap between current visible and the section's claimed totalCount.
   *  Until the paginated endpoint lands, fixture supply caps this. */
  remainingInSection(sectionId: string, cardsLength: number): number {
    const visible = this.visibleCounts[sectionId] || 0;
    return Math.max(0, cardsLength - visible);
  }

  trackByPlate(_: number, card: PlateCard): string {
    return card.plate;
  }

  trackBySection(_: number, section: PerfectPlatesSection): string {
    return section.id;
  }

  /**
   * BYO search — calls the same backend `process=byo` endpoint the legacy
   * /build-your-own page uses (via `SearchService.searchBYO`), so the
   * result set is DVLA's actual inventory rather than the curated wordbank.
   * For `JE76 O??` the backend returns 573 rows — matching DVLA and the
   * existing /build-your-own count.
   *
   * The earlier client-side-regex approach returned only the ~16 word-match
   * combinations because the wordbank is a curated subset, not the full
   * DVLA inventory. Replaced wholesale per Hugh's instruction.
   *
   * Mode 2 returns `[]` for `process=byo`. `searchBYO` was updated to use
   * the canonical URL placeholder so on dev2 it reaches the real plato-
   * search Lambda directly — same fix pattern as #39 / #43.
   */
  onByoSearch(submission: ByoSubmit): void {
    const cells = submission.pattern;
    const toApi = (s: string) => (s === '_' ? '-' : s);
    const prefix  = cells.slice(0, 2).map(toApi).join('');
    const numbers = cells.slice(2, 4).join('');
    const letters = cells.slice(4, 7).map(toApi).join('');

    // Pre-release: DVLA stock doesn't exist yet, so `process=byo` would come
    // back empty and read as "no plates". Show the composed plate with
    // price TBC instead (template renders the pre-release note).
    if (this.data?.series.preRelease) {
      this.byoLoading = false;
      this.byoSearchTerm = `${prefix}${numbers} ${letters}`.replace(/-/g, '?');
      this.byoResults = new SearchReqResponse('byo_new_release', []);
      return;
    }

    this.byoLoading = true;
    this.byoResults = null;
    // Display term — replace dashes with ? for human readability ("AB76 ??C").
    this.byoSearchTerm = `${prefix}${numbers} ${letters}`.replace(/-/g, '?');

    this.searchService.searchBYO('current', prefix, numbers, letters, (results: Registration[]) => {
      this.byoLoading = false;
      const formatted = this.registrationService.formatRegistrations(results || []);
      this.byoResults = new SearchReqResponse('byo_new_release', formatted);
      this.byoResultChange.emit(this.byoResults);
    });
  }

  /** Clear BYO inline results and return to the default page state. */
  clearByoSearch(): void {
    this.byoResults = null;
    this.byoLoading = false;
    this.byoSearchTerm = '';
  }

  /** Route a BYO result registration to its compare detail page. */
  onByoResultClick(reg: Registration): void {
    const r = (reg?.registration || '').replace(/\s+/g, '');
    if (!r) return;
    this.router.navigate(['/plate', r]);
  }

  /** Banned-plate pill → compare. Strips the wildcard form before routing —
   *  patterns like `BJ76 AY*` aren't real registrations and route nowhere. */
  onBannedPlateClick(plate: string): void {
    if (!plate || plate.indexOf('*') > -1) return;
    const reg = plate.replace(/\s+/g, '');
    if (!reg) return;
    this.router.navigate(['/plate', reg]);
  }

  /**
   * Track button: the shared tracking system (TrackPlateService), the same one
   * as the plate page's Track plate. Tracked → second tap stops tracking.
   * Signed out, the sign-in box opens and the track completes after sign-in.
   */
  onTrack(card: PlateCard): void {
    if (!card?.plate) return;
    const reg = card.plate;
    const display = card.plate;
    if (this.trackPlate.isBusy(reg)) return;
    if (this.trackPlate.isTracked(reg)) {
      // Confirm before stopping, so a stray tap never silently drops tracking.
      this.snackBar
        .open(`Stop tracking ${display}?`, 'Stop tracking', { duration: 5000 })
        .onAction()
        .subscribe(() =>
          this.trackPlate.untrack(reg).subscribe((ok) => {
            if (ok) this.snackBar.open(`Stopped tracking ${display}`, 'Dismiss', { duration: 2500 });
          })
        );
      return;
    }
    this.trackPlate.track(reg, display, (ok) => {
      if (ok) this.snackBar.open(`Tracking ${display}`, 'Dismiss', { duration: 2500 });
    });
  }


  /**
   * Save button: the shared shortlist (SavedPlatesService), no sign-in needed.
   * Independent from Track: a plate can be Tracked, Saved, both, or neither.
   */
  onFavourite(card: PlateCard): void {
    if (!card?.plate) return;
    const display = card.plate;
    const wasSaved = this.savedPlates.isSaved(display);
    this.savedPlates.toggle(display).subscribe((ok) => {
      if (!ok) return;
      this.snackBar.open(
        wasSaved ? `Removed ${display} from saved plates` : `Saved ${display}`,
        'Dismiss',
        { duration: 2500 },
      );
    });
  }

  onBannedClick(plate: string): void {
    if (!plate || plate.includes('*')) return;  // asterisk = wildcard, no canonical reg to route to
    const reg = plate.replace(/\s+/g, '');
    if (!reg) return;
    this.router.navigate(['/plate', reg]);
  }

  isWildcardPlate(plate: string): boolean {
    return !!plate && plate.includes('*');
  }

  onAzLetter(letter: string | null): void {
    this.selectedLetter = letter;
    // Reset the names visible count so the filtered list shows from page 1.
    this.visibleCounts['names'] = this.PAGE_SIZE;
    // Scroll the grid into view so the filter result is immediately visible.
    setTimeout(() => {
      const grid = document.getElementById('names-grid');
      if (grid) {
        grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  }
}
